/**
 * Hosting windows -- the product's anti-abuse mechanism.
 *
 * The founder buys a span of hosting time, not a page. Because the launch date
 * must fall inside the window they paid for, the "pay $10 and set a date 30
 * years out" exploit closes by construction rather than by a special rule.
 */
import { addMonths } from './time.js'

export type WindowId = 'window_3mo' | 'window_6mo' | 'window_12mo'

export interface HostingWindow {
  readonly id: WindowId
  readonly months: number
  readonly priceCents: number
  readonly label: string
}

/** `id` doubles as the Stripe Price `lookup_key`, so the catalog is code-defined. */
export const HOSTING_WINDOWS: readonly HostingWindow[] = [
  { id: 'window_3mo', months: 3, priceCents: 900, label: '3 months' },
  { id: 'window_6mo', months: 6, priceCents: 1500, label: '6 months' },
  { id: 'window_12mo', months: 12, priceCents: 2500, label: '12 months' },
]

export const MAX_WINDOW_MONTHS = 12

export function findWindow(id: string): HostingWindow | undefined {
  return HOSTING_WINDOWS.find((w) => w.id === id)
}

/** Expiry for a fresh purchase. */
export function computeExpiry(paidAt: Date, window: HostingWindow): Date {
  return addMonths(paidAt, window.months)
}

/**
 * Expiry after an extension.
 *
 * Extends from the current expiry, not from now, so someone who renews early
 * is not punished by losing the remainder of their existing window. If the
 * window has already lapsed, extend from now instead.
 */
export function extendExpiry(currentExpiry: Date, window: HostingWindow, now: Date): Date {
  const base = currentExpiry.getTime() > now.getTime() ? currentExpiry : now
  return addMonths(base, window.months)
}

export type LaunchDateRejection = 'in-past' | 'after-expiry'

export type LaunchDateCheck =
  | { readonly ok: true }
  | { readonly ok: false; readonly reason: LaunchDateRejection }

/**
 * The core guardrail: `now < launchAt <= expiresAt`.
 *
 * Enforced server-side on publish AND on every edit. Client-side checks are a
 * convenience; this is the rule that actually holds.
 */
export function validateLaunchDate(launchAt: Date, now: Date, expiresAt: Date): LaunchDateCheck {
  if (launchAt.getTime() <= now.getTime()) return { ok: false, reason: 'in-past' }
  if (launchAt.getTime() > expiresAt.getTime()) return { ok: false, reason: 'after-expiry' }
  return { ok: true }
}
